import axios from './client'
import {
  ReleaseFilters,
  ReleasesResponse,
  ReleaseDetailResponse,
  ReleasesStatsResponse,
  GenresResponse,
  StylesResponse,
  CountriesResponse,
  FacetsResponse
} from '../types/release'

function buildReleaseParams(filters: Omit<ReleaseFilters, 'release_ids' | 'seller'>): URLSearchParams {
  const params = new URLSearchParams()

  Object.entries(filters).forEach(([key, value]) => {
    if (value == null) return
    if (Array.isArray(value)) {
      value.forEach(item => params.append(key, String(item)))
    } else {
      params.set(key, String(value))
    }
  })

  return params
}

export async function getReleases(filters: ReleaseFilters = {}): Promise<ReleasesResponse> {
  const { release_ids, seller, ...rest } = filters
  const params = buildReleaseParams(rest)
  let data: any
  if (release_ids && release_ids.length > 0) {
    // Use POST to avoid URL length limits with large ID lists
    const res = await axios.post('/releases/search', { release_ids, seller }, { params })
    data = res.data
  } else {
    const res = await axios.get('/releases', { params })
    data = res.data
  }

  return data
}

export async function getRelease(id: number): Promise<ReleaseDetailResponse> {
  const { data } = await axios.get(`/releases/${id}`)
  return data
}

export async function getAudioTags(id: number): Promise<{
  success: boolean
  has_audio: boolean
  predicted_genres?: Record<string, number>
  predicted_styles?: Record<string, number>
  confidence?: number
  tag_agreement?: number
}> {
  const { data } = await axios.get(`/releases/${id}/audio-tags`)
  return data
}

export interface VideoAudio {
  bpm: number | null
  key: string | null
  bpm_confidence?: number | null
  key_strength?: number | null
  styles: string[]
  instruments: string[]
  moods: string[]
}

export async function getVideoAudio(id: number): Promise<{
  success: boolean
  by_video_id: Record<string, VideoAudio>
}> {
  const { data } = await axios.get(`/releases/${id}/video-audio`)
  return data
}

export async function getReleasesStats(): Promise<ReleasesStatsResponse> {
  const { data } = await axios.get('/releases/stats')
  return data
}

export async function getGenres(): Promise<GenresResponse> {
  const { data } = await axios.get('/releases/genres')
  return data
}

export async function getStyles(): Promise<StylesResponse> {
  const { data } = await axios.get('/releases/styles')
  return data
}

export async function getCountries(): Promise<CountriesResponse> {
  const { data } = await axios.get('/releases/countries')
  return data
}

export async function getReleaseFacets(
  filters: Omit<ReleaseFilters, 'genres' | 'styles' | 'page' | 'per_page' | 'sort' | 'order' | 'shuffle'>
): Promise<FacetsResponse> {
  const { data } = await axios.post('/releases/facets', filters)
  return data
}

export async function getReleaseNeighbors(
  id: number,
  limit = 12,
  includeSameArtist = false
): Promise<{ success: boolean; releases: import('../types/release').Release[]; using_fallback: boolean; empty?: boolean }> {
  const { data } = await axios.get(`/releases/${id}/audio-neighbors`, { params: { limit, include_same_artist: includeSameArtist } })
  return data
}

export async function removeCollectionDuplicates(): Promise<{ success: boolean; removed: number; releaseIds: number[]; message: string }> {
  const { data } = await axios.post('/discogs/collection/remove-duplicates')
  return data
}

export async function refreshReleaseStats(id: number): Promise<{ success: boolean; updated?: boolean; videos?: any[]; stats?: Record<string, number | null> }> {
  const { data } = await axios.post(`/discogs/releases/${id}/refresh-stats`)
  return data
}

export async function getRecommendations(
  sourceIds: number[],
  filters: Omit<ReleaseFilters, 'release_ids' | 'q'> = {},
  useCollectionTaste = false
): Promise<ReleasesResponse> {
  const { data } = await axios.post('/releases/recommendations', {
    source_ids: sourceIds,
    ...filters,
    ...(useCollectionTaste && { use_collection_taste: true }),
  })
  return data
}
